import {
    Avatar,
    cardBoxShadow,
    cardTransition,
    Colors,
    defaultAnimation,
    defaultHover,
    Icon,
    Typography,
} from '@amelio/ui-library';
import { useState } from 'react';

import type { AnswerEmojiItemProps } from './types';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';

/**
 * Padding styles mapping for emoji cards
 */
const CARD_PADDING_STYLES = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
} as const;

/**
 * Border radius styles mapping for emoji cards
 */
const CARD_BORDER_RADIUS_STYLES = {
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
} as const;

/**
 * Additional hover effects for emoji cards
 * (scale - used alongside defaultHover)
 */
const CARD_HOVER_EFFECTS = ['hover:scale-110'];

/**
 * AnswerEmojiItem component
 *
 * A presentational component that renders a single emoji option card.
 * Handles the visual representation and interaction for one emoji option.
 *
 * @example
 * ```tsx
 * <AnswerEmojiItem
 *   emoji={emojiOption}
 *   isSelected={true}
 *   disabled={false}
 *   onClick={() => handleClick(emojiOption)}
 *   onKeyDown={(e) => handleKeyDown(e)}
 *   buttonRef={(el) => buttonRefs.current[0] = el}
 *   cardPadding="md"
 *   cardBorderRadius="lg"
 *   unselectedBgColor="bg-base-50"
 *   selectedBorderColor={Colors.PRIMARY}
 *   unselectedTextColor={Colors.BASE}
 *   avatarSize="md"
 *   avatarVariant="circular"
 *   labelVariant="body-small"
 *   dataTestId="emoji-0"
 * />
 * ```
 */
export const AnswerEmojiItem = ({
    emoji,
    isSelected,
    disabled,
    onClick,
    onKeyDown,
    buttonRef,
    cardPadding,
    cardBorderRadius,
    unselectedBgColor,
    selectedBorderColor,
    unselectedTextColor,
    avatarSize,
    avatarVariant,
    labelVariant,
    isMobile = false,
    dataTestId,
}: AnswerEmojiItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const baseCardStyles = [
        'flex flex-col items-center justify-center',
        isMobile ? 'gap-1' : 'gap-2',
        CARD_BORDER_RADIUS_STYLES[cardBorderRadius],
        CARD_PADDING_STYLES[cardPadding],
        'border',
        'relative',
        isMobile ? 'min-w-28' : 'min-w-36',
        unselectedBgColor,
        cardTransition,
        defaultHover,
        ...CARD_HOVER_EFFECTS,
    ]
        .filter(Boolean)
        .join(' ');

    const stateCardStyles = isSelected
        ? ['font-medium'].filter(Boolean).join(' ')
        : ['border-base-200', 'hover:border-base-300', `hover:[color:${Colors.PRIMARY}]`]
              .filter(Boolean)
              .join(' ');

    const disabledCardStyles = disabled ? 'cursor-not-allowed opacity-50' : '';

    const cardCombinedStyles = [baseCardStyles, stateCardStyles, disabledCardStyles]
        .filter(Boolean)
        .join(' ');

    return (
        <button
            ref={buttonRef}
            type={'button'}
            onClick={onClick}
            onKeyDown={onKeyDown}
            disabled={disabled}
            className={cardCombinedStyles}
            onMouseEnter={() => !disabled && !isSelected && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                ...(isSelected && {
                    borderColor: selectedBorderColor,
                    boxShadow: cardBoxShadow,
                }),
                ...(!isSelected &&
                    isHovered && {
                        boxShadow: cardBoxShadow,
                    }),
            }}
            aria-pressed={isSelected}
            aria-label={`${emoji.label}${isSelected ? ', selected' : ''}`}
            data-testid={dataTestId}
            data-selected={isSelected}
        >
            {isSelected && (
                <div
                    className={`bg-base-50 absolute -top-1 -right-1 z-10 flex items-center justify-center rounded-full shadow-sm ${defaultAnimation}`}
                    data-testid={dataTestId ? `${dataTestId}-checkmark` : undefined}
                >
                    <Icon
                        name={'essential-checkmark'}
                        size={'lg'}
                        color={Colors.PRIMARY}
                        className={'p-0.5 font-bold'}
                    />
                </div>
            )}

            <Avatar
                src={emoji.src}
                alt={emoji.alt || emoji.label}
                size={avatarSize}
                variant={avatarVariant}
                borderSize={'none'}
                interactive={false}
                dataTestId={dataTestId ? `${dataTestId}-avatar` : undefined}
            />

            <div className={'text-center'}>
                {/* align is set explicitly: Typography defaults to 'left' and puts text-left on its own
                    element, which wins over the text-center inherited from this wrapper. Without it, a
                    label that wraps ("Far below expectations") aligns its lines to the left. */}
                <Typography
                    variant={labelVariant}
                    color={unselectedTextColor}
                    align={'center'}
                >
                    <QuestionTextRenderer text={emoji.label} />
                </Typography>
            </div>
        </button>
    );
};
